import React, { useState, useRef, useEffect } from 'react';
import { aiAPI } from '../services/aiBackend';

const AIChatPrompt = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Bonjour ! Je suis votre assistant IA connecté aux vraies APIs pour La Soirée du Rire. Je peux vous aider à créer du contenu engageant, analyser vos performances Facebook en temps réel et optimiser votre stratégie de réseaux sociaux. Comment puis-je vous aider aujourd'hui ?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [configStatus, setConfigStatus] = useState({});
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Check configuration status when component mounts
    checkConfigStatus();
  }, []);

  const checkConfigStatus = async () => {
    try {
      const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/config/status');
      if (response.ok) {
        const data = await response.json();
        setConfigStatus(data.config || {});
      }
    } catch (error) {
      console.error('Failed to fetch configuration status:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Determine the type of request and call appropriate AI service
      let aiResponse;
      
      if (inputMessage.toLowerCase().includes('générer') || inputMessage.toLowerCase().includes('créer')) {
        // Content generation request
        aiResponse = await aiAPI.generateContent({
          prompt: inputMessage,
          platform: 'facebook',
          contentType: 'post',
          maxLength: 500,
          includeHashtags: false,
          includeEmojis: true,
          userId: 'dashboard-user'
        });
      } else if (inputMessage.toLowerCase().includes('analyser') || inputMessage.toLowerCase().includes('insight')) {
        // Analytics insight request
        aiResponse = await aiAPI.getInsights({
          query: inputMessage,
          dataType: 'analytics',
          platform: 'facebook',
          userId: 'dashboard-user'
        });
      } else if (inputMessage.toLowerCase().includes('idée') || inputMessage.toLowerCase().includes('contenu')) {
        // Content ideas request
        aiResponse = await aiAPI.generateContentIdeas({
          platform: 'facebook',
          theme: 'spectacle humour',
          count: 5,
          userId: 'dashboard-user'
        });
      } else if (inputMessage.toLowerCase().includes('optimiser') || inputMessage.toLowerCase().includes('améliorer')) {
        // Content optimization request
        aiResponse = await aiAPI.optimizeContent({
          content: inputMessage,
          platform: 'facebook',
          targetAudience: 'spectateurs de spectacle d\'humour',
          goals: ['engagement', 'partage']
        });
      } else {
        // General chat/help request
        aiResponse = await aiAPI.generateContent({
          prompt: `Vous êtes un assistant IA spécialisé pour La Soirée du Rire, un spectacle d'humour. L'utilisateur a demandé : "${inputMessage}". Fournissez une réponse utile et professionnelle liée aux réseaux sociaux, à la création de contenu, aux analyses ou à la stratégie marketing pour ce spectacle.`,
          platform: 'facebook',
          contentType: 'response',
          maxLength: 500,
          includeHashtags: false,
          includeEmojis: true,
          userId: 'dashboard-user'
        });
      }

      // Handle different response formats
      let responseContent = '';
      if (aiResponse.success !== false) {
        responseContent = aiResponse.generatedContent || 
                         aiResponse.content || 
                         aiResponse.insights?.join('\n') || 
                         aiResponse.ideas?.join('\n') || 
                         aiResponse.optimizedContent ||
                         aiResponse.message || 
                         'Réponse générée avec succès.';
      } else {
        responseContent = aiResponse.generatedContent || 
                         aiResponse.content || 
                         'Je m\'excuse, mais je n\'ai pas pu générer de réponse pour le moment. Veuillez réessayer.';
      }

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: responseContent,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      
      // Provide helpful fallback responses based on the user's question
      let fallbackResponse = '';
      
      if (inputMessage.toLowerCase().includes('générer') || inputMessage.toLowerCase().includes('créer')) {
        const hasOpenAI = configStatus.openai?.configured || false;
        if (hasOpenAI) {
          fallbackResponse = `Voici quelques idées de contenu pour La Soirée du Rire :

🎭 **Publications Facebook :**
• "Préparez-vous à rire jusqu'à en avoir mal aux côtes ! 🎭😂"
• "La programmation de ce soir est absolument hilarante ! Ne la ratez pas ! 🎪"
• "De l'or comique vous attend à La Soirée du Rire ! ✨"

✨ **Génération IA activée !** Je peux créer du contenu personnalisé pour vous.`;
        } else {
          fallbackResponse = `Voici quelques idées de contenu pour La Soirée du Rire :

🎭 **Publications Facebook :**
• "Préparez-vous à rire jusqu'à en avoir mal aux côtes ! 🎭😂"
• "La programmation de ce soir est absolument hilarante ! Ne la ratez pas ! 🎪"
• "De l'or comique vous attend à La Soirée du Rire ! ✨"

🔧 Pour activer la génération de contenu IA complète, veuillez configurer la clé API OpenAI dans votre déploiement.`;
        }
      } else if (inputMessage.toLowerCase().includes('analyser') || inputMessage.toLowerCase().includes('insight')) {
        const hasOpenAI = configStatus.openai?.configured || false;
        if (hasOpenAI) {
          fallbackResponse = `Je peux vous aider à analyser les performances de vos réseaux sociaux ! Voici quelques métriques clés à suivre :

📊 **Métriques d'Engagement :**
• J'aime, commentaires et partages
• Portée et impressions
• Taux de clics

📈 **Métriques de Croissance :**
• Taux de croissance des abonnés
• Tendances de performance des publications
• Meilleurs moments de publication

🎯 **Performance du Contenu :**
• Publications les plus performantes
• Démographie de l'audience
• Insights spécifiques à la plateforme

✨ **Analyse IA activée !** Je peux fournir des insights avancés basés sur vos données.`;
        } else {
          fallbackResponse = `Je peux vous aider à analyser les performances de vos réseaux sociaux ! Voici quelques métriques clés à suivre :

📊 **Métriques d'Engagement :**
• J'aime, commentaires et partages
• Portée et impressions
• Taux de clics

📈 **Métriques de Croissance :**
• Taux de croissance des abonnés
• Tendances de performance des publications
• Meilleurs moments de publication

🎯 **Performance du Contenu :**
• Publications les plus performantes
• Démographie de l'audience
• Insights spécifiques à la plateforme

🔧 Pour obtenir des insights d'analyse alimentés par IA, veuillez configurer les clés API dans votre déploiement.`;
        }
      } else {
        const hasOpenAI = configStatus.openai?.configured || false;
        if (hasOpenAI) {
          fallbackResponse = `Bonjour ! Je suis votre assistant IA pour La Soirée du Rire. Je peux vous aider avec :

🎭 **Création de Contenu :** Générer des publications et légendes engageantes
📊 **Analyses :** Analyser les performances de vos réseaux sociaux  
📅 **Planification :** Optimiser vos moments de publication
💡 **Stratégie :** Fournir des insights pour la croissance

✨ **Toutes les capacités IA sont activées !** Je peux vous aider avec du contenu personnalisé et des analyses avancées.

Que souhaitez-vous savoir sur la stratégie des réseaux sociaux de votre spectacle d'humour ?`;
        } else {
          fallbackResponse = `Bonjour ! Je suis votre assistant IA pour La Soirée du Rire. Je peux vous aider avec :

🎭 **Création de Contenu :** Générer des publications et légendes engageantes
📊 **Analyses :** Analyser les performances de vos réseaux sociaux  
📅 **Planification :** Optimiser vos moments de publication
💡 **Stratégie :** Fournir des insights pour la croissance

🔧 Actuellement, je fonctionne en mode démo. Pour débloquer toutes les capacités IA, veuillez configurer les clés API dans votre déploiement.

Que souhaitez-vous savoir sur la stratégie des réseaux sociaux de votre spectacle d'humour ?`;
        }
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: fallbackResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="ai-chat-overlay" onClick={onClose}>
      <div className="ai-chat-container" onClick={(e) => e.stopPropagation()}>
        <div className="ai-chat-header">
          <div className="ai-chat-title">
            <div className="ai-avatar">🤖</div>
            <div>
              <h3>Assistant IA</h3>
              <p>La Soirée du Rire</p>
            </div>
          </div>
          <button className="ai-chat-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="ai-chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`ai-message ${message.type}`}>
              <div className="ai-message-content">
                {message.content}
              </div>
              <div className="ai-message-time">
                {formatTime(message.timestamp)}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="ai-message ai">
              <div className="ai-message-content">
                <div className="ai-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="ai-chat-input" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Comment puis-je vous aider avec votre stratégie de réseaux sociaux ?"
            disabled={isLoading}
          />
          <button type="submit" disabled={!inputMessage.trim() || isLoading}>
            {isLoading ? '⏳' : '➤'}
          </button>
        </form>

        <div className="french-suggestions">
          <button className="french-suggestion-chip" onClick={() => setInputMessage("Génère une publication Facebook pour promouvoir le spectacle")}>
            Générer du Contenu
          </button>
          <button className="french-suggestion-chip" onClick={() => setInputMessage("Analyse les performances Facebook en temps réel")}>
            Analytics Temps Réel
          </button>
          <button className="french-suggestion-chip" onClick={() => setInputMessage("Donne-moi des idées de contenu pour Facebook")}>
            Idées de Contenu
          </button>
          <button className="french-suggestion-chip" onClick={() => setInputMessage("Optimise cette publication pour plus d'engagement")}>
            Optimiser Contenu
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatPrompt;